/**
 * SignalR Retry Policy
 * Infinite retry with exponential backoff capped at 30s.
 * Used by both comments and presence hubs.
 */
import type { IRetryPolicy, RetryContext } from '@microsoft/signalr';

const INITIAL_DELAYS = [0, 2000, 5000, 10000];
const MAX_DELAY = 30_000;

export const signalrRetryPolicy: IRetryPolicy = {
  nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
    if (retryContext.previousRetryCount < INITIAL_DELAYS.length) {
      return INITIAL_DELAYS[retryContext.previousRetryCount] ?? MAX_DELAY;
    }
    return MAX_DELAY;
  },
};

const RETRY_DELAYS = [5000, 10000, 30_000];

function getRetryDelay(attempt: number): number {
  return RETRY_DELAYS[Math.min(attempt, RETRY_DELAYS.length - 1)] ?? MAX_DELAY;
}

/**
 * Retry `connection.start()` with exponential backoff.
 * Returns when connected or when `shouldAbort()` returns true.
 * Safe to call multiple times — concurrent calls are no-ops.
 */
export async function retryStart(
  start: () => Promise<void>,
  shouldAbort: () => boolean,
  onConnected?: () => void
): Promise<void> {
  let attempt = 0;

  while (!shouldAbort()) {
    const delay = getRetryDelay(attempt);
    await new Promise((resolve) => setTimeout(resolve, delay));
    if (shouldAbort()) return;

    try {
      await start();
      onConnected?.();
      return;
    } catch {
      attempt++;
    }
  }
}
